import React, { useEffect, useMemo, useState } from "react";
import { memberApi, useMemberAuth } from "@/lib/memberAuth";
import { toast } from "sonner";
import { CalendarBlank, Plus, CaretLeft, CaretRight, Trash } from "@phosphor-icons/react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function firstWeekday(year, month) {
  return new Date(year, month, 1).getDay(); // 0=Sunday
}

const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2) return "";
  const A = 0x1F1E6;
  return String.fromCodePoint(A + cc.toUpperCase().charCodeAt(0) - 65) +
         String.fromCodePoint(A + cc.toUpperCase().charCodeAt(1) - 65);
};

export default function MemberHolidays() {
  const { member } = useMemberAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-11
  const [holidays, setHolidays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    country: member?.country || "",
    country_code: "",
    date: "",
    name: "",
    note: "",
  });

  const load = () => {
    memberApi.get(`/portal/holidays?year=${year}`)
      .then(({ data }) => setHolidays(data.holidays || []))
      .catch(() => toast.error("Could not load holidays"));
  };
  useEffect(() => { load(); }, [year]);

  const byDate = useMemo(() => {
    const m = {};
    for (const h of holidays) (m[h.date] = m[h.date] || []).push(h);
    return m;
  }, [holidays]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.country || !form.date || !form.name) {
      toast.error("Country, date and name are required.");
      return;
    }
    try {
      await memberApi.post("/portal/holidays", form);
      toast.success("Holiday added.");
      // Jump the calendar to the added holiday's month for immediate visual confirmation
      const [y, m] = form.date.split("-").map(Number);
      if (!Number.isNaN(y) && !Number.isNaN(m)) {
        setYear(y);
        setMonth(m - 1);
      }
      setForm({ country: member?.country || "", country_code: "", date: "", name: "", note: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save holiday");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this holiday?")) return;
    try {
      await memberApi.delete(`/portal/holidays/${id}`);
      load();
    } catch { toast.error("Could not delete"); }
  };

  const dim = daysInMonth(year, month);
  const start = firstWeekday(year, month);
  const cells = [...Array(start).fill(null), ...Array.from({ length: dim }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  while (cells.length < 42) cells.push(null);

  const goto = (delta) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setMonth(m); setYear(y);
  };

  const dateKey = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div data-testid="portal-holidays">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1">Portal</div>
          <h1 className="font-display text-3xl font-bold text-navy tracking-tight flex items-center gap-2">
            <CalendarBlank size={24} weight="duotone" className="text-ember" /> Worldwide Holiday Calendar
          </h1>
          <p className="text-sm text-navy/70 mt-2 max-w-2xl">
            Add your country's public holidays so members planning shipments through {member?.country} don't book
            pickups on your closed days.
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="holidays-add-btn"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-navy text-white px-3 py-2 hover:bg-navy/90">
          <Plus size={12} /> Add holiday
        </button>
      </header>

      {showForm && (
        <form onSubmit={handleSubmit} className="border border-black/10 bg-white p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="holidays-form">
          <label className="block text-xs uppercase tracking-widest text-navy/60">Country *
            <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="input mt-1" data-testid="holiday-country" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">ISO country code (2 letters)
            <input value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase().slice(0, 2) })} className="input mt-1" placeholder="SI, DE, TR…" data-testid="holiday-cc" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Date *
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input mt-1" data-testid="holiday-date" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Name *
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input mt-1" placeholder="e.g. Statehood Day" data-testid="holiday-name" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60 md:col-span-2">Note
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="input mt-1" placeholder="e.g. Offices closed 24h" />
          </label>
          <div className="md:col-span-2 flex gap-2">
            <button type="submit" className="bg-navy text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy/90" data-testid="holiday-submit">Save</button>
            <button type="button" onClick={() => setShowForm(false)} className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest">Cancel</button>
          </div>
        </form>
      )}

      <section className="border border-black/10 bg-white">
        <div className="flex items-center justify-between px-5 py-3 border-b border-black/10 bg-navy text-white">
          <button onClick={() => goto(-1)} data-testid="holidays-prev" className="text-white/80 hover:text-ember"><CaretLeft size={18} /></button>
          <div className="uppercase tracking-widest font-medium" data-testid="holidays-current-month">{MONTHS[month]} {year}</div>
          <button onClick={() => goto(1)} data-testid="holidays-next" className="text-white/80 hover:text-ember"><CaretRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 border-b border-black/10 bg-ember/20 text-navy text-xs uppercase tracking-widest">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="px-2 py-2 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const key = d ? dateKey(d) : null;
            const list = (key && byDate[key]) || [];
            return (
              <div key={i} className={`border-b border-r border-black/5 min-h-[92px] p-2 ${d ? "bg-white" : "bg-black/[0.02]"}`}>
                {d && (
                  <>
                    <div className="text-xs text-navy/50 mb-1">{String(d).padStart(2, "0")}</div>
                    <div className="flex flex-wrap gap-1">
                      {list.map((h) => (
                        <button key={h.id} onClick={() => handleDelete(h.id)} title={`${h.name}${h.note ? ` — ${h.note}` : ""} (click to remove)`}
                          data-testid={`holiday-pin-${h.id}`}
                          className="text-lg leading-none hover:opacity-50 transition-opacity">
                          {flagEmoji(h.country_code) || <span className="text-[9px] font-medium bg-ember/30 px-1">{h.country.slice(0, 3).toUpperCase()}</span>}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-6" data-testid="holidays-list">
        <div className="overline mb-2">All holidays in {year}</div>
        {holidays.length === 0 ? (
          <div className="text-sm text-navy/50">Nothing on file for {year} yet.</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {holidays.map((h) => (
              <li key={h.id} className="border border-black/10 bg-white p-3 flex items-center justify-between" data-testid={`holiday-item-${h.id}`}>
                <div>
                  <span className="mr-2">{flagEmoji(h.country_code)}</span>
                  <span className="font-medium">{h.name}</span>
                  <span className="text-navy/50"> · {h.country} · {h.date}</span>
                </div>
                <button onClick={() => handleDelete(h.id)} className="text-navy/40 hover:text-red-600" data-testid={`holiday-del-${h.id}`}>
                  <Trash size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
